import { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

interface HomepageStats {
    activeManufacturers: number;
    productListings: number;
    successfulConnections: number;
    averageResponseTime: number;
}

export function useHomepageStats() {
    const [stats, setStats] = useState<HomepageStats>({
        activeManufacturers: 0,
        productListings: 0,
        successfulConnections: 0,
        averageResponseTime: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const supabase = createClient();

                // Fetch active manufacturers count
                const { count: mfrCount, error: mfrError } = await supabase
                    .from('manufacturers')
                    .select('*', { count: 'exact', head: true })
                    .eq('is_active', true);

                if (mfrError) throw mfrError;

                // Fetch product listings count
                const { count: productCount, error: productError } = await supabase
                    .from('products')
                    .select('*', { count: 'exact', head: true })
                    .eq('is_active', true);

                if (productError) throw productError;

                // Fetch successful connections count (using closed/responded inquiries as proxy)
                const { count: connectionCount, error: connectionError } = await supabase
                    .from('inquiries')
                    .select('*', { count: 'exact', head: true })
                    .in('status', ['responded', 'closed']);

                if (connectionError) {
                    console.warn('Could not fetch connections:', connectionError);
                }

                // Calculate average response time from inquiries
                // Get inquiries that have been responded to
                const { data: respondedInquiries, error: avgError } = await supabase
                    .from('inquiries')
                    .select('created_at, updated_at')
                    .eq('status', 'responded');

                if (avgError) {
                    console.warn('Could not fetch response times:', avgError);
                }

                // Calculate average response time in hours
                let avgResponseTime = 24; // Default to 24 hours if no data
                if (respondedInquiries && respondedInquiries.length > 0) {
                    const responseTimes = respondedInquiries
                        .filter(inq => inq.created_at && inq.updated_at)
                        .map(inq => {
                            const created = new Date(inq.created_at).getTime();
                            const updated = new Date(inq.updated_at).getTime();
                            return (updated - created) / (1000 * 60 * 60); // Convert to hours
                        })
                        .filter(hours => hours > 0 && hours < 720); // Filter out invalid times (0 or > 30 days)

                    if (responseTimes.length > 0) {
                        avgResponseTime = responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length;
                    }
                }

                setStats({
                    activeManufacturers: mfrCount || 0,
                    productListings: productCount || 0,
                    successfulConnections: connectionCount || 0,
                    averageResponseTime: Math.round(avgResponseTime * 10) / 10, // Round to 1 decimal
                });
            } catch (err: any) {
                console.error('Error fetching homepage stats:', err);

                // Check if error is due to missing tables
                if (err?.message?.includes('relation') || err?.code === '42P01') {
                    console.warn('Database tables not found. Please run migration scripts.');
                }
                setError(err instanceof Error ? err.message : 'Failed to fetch stats');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    return { stats, loading, error };
}
